import OfferInclusionsList from 'components/Common/Inclusions/OfferInclusionsList'
import Modal from 'LuxKit/Modal/Modal'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import SearchTileStackAmenitiesList from '../shared/SearchTileStackAmenitiesList'
import SearchTileStackInclusionsList from '../shared/SearchTileStackInclusionsList'
import SearchTileRoomBreakdown from '../shared/SearchTileRoomBreakdown'
import useOfferUrl from 'hooks/Offers/useOfferUrl'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import { reorderTileInclusionsByActiveFilters } from 'lib/offer/inclusionUtils'
import { useActiveInclusionFilters } from 'hooks/useActiveInclusionFilters'
import LoyaltyInclusions from 'loyalty/components/LoyaltyInclusions'
import useLoyaltyBenefitInclusions from 'loyalty/hooks/useLoyaltyBenefitInclusions'
import { CHARTER_FLIGHT_DETAILS, MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import { rewriteCharterReturnFlightsGuestInclusions } from 'lib/charterFlights/charterBundleOfferPageUtils'
import { EmptyArray } from 'lib/array/arrayUtils'

const DEFAULT_CHARTER_SEARCH_ROOMS: Array<App.Occupants> = [{ adults: 2 }]

const MAX_LUX_PLUS_INCLUSIONS_TO_DISPLAY = 5
const MAX_REGULAR_INCLUSIONS_TO_DISPLAY = 2
const MAX_TOTAL_INCLUSIONS_TO_DISPLAY = MAX_LUX_PLUS_INCLUSIONS_TO_DISPLAY + MAX_REGULAR_INCLUSIONS_TO_DISPLAY

interface Props {
  bestPackage?: App.HotelPackage
  filters?: App.OfferListFilters
  offer: App.HotelOffer | App.HotelOfferSummary
  offerMetaData?: App.OfferListMetaData
  doNotOpenInclusionsModal?: boolean
  maxTotalInclusions?: number
  hideFeatureTitle?: boolean
  hideRoomBreakdown?: boolean
  maxAmenitiesToShow?: number
  hideLuxPlusLabels?: boolean
}

function HotelSearchTileFeatures({
  bestPackage,
  filters,
  offer,
  offerMetaData,
  doNotOpenInclusionsModal = false,
  maxTotalInclusions: maxTotalInclusionsOverride,
  hideFeatureTitle = false,
  hideRoomBreakdown = false,
  maxAmenitiesToShow,
  hideLuxPlusLabels = false,
}: Props) {
  const intl = useIntl()
  const maxTotalInclusions = maxTotalInclusionsOverride ?? MAX_TOTAL_INCLUSIONS_TO_DISPLAY

  const charterRouteInfo = CHARTER_FLIGHT_DETAILS['melbourne-maldives']
  const isCharterBundleOffer = offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)

  const { luxPlusInclusions } = useOfferInclusions(
    bestPackage,
    {
      checkIn: filters?.checkIn,
      checkOut: filters?.checkOut,
    },
  )

  const activeInclusionCategories = useActiveInclusionFilters()

  const luxPlusSearchTileInclusions = useMemo<Array<App.PackageInclusion>>(() => {
    if (offer.luxPlusTileInclusions?.length) {
      return offer.luxPlusTileInclusions
    }
    return luxPlusInclusions
  }, [luxPlusInclusions, offer.luxPlusTileInclusions])
  const displayedLuxPlusSearchTileInclusions = useMemo<Array<App.PackageInclusion>>(() => {
    return hideLuxPlusLabels ? EmptyArray : luxPlusSearchTileInclusions
  }, [hideLuxPlusLabels, luxPlusSearchTileInclusions])

  const getEffectiveInclusions = useCallback(<T extends { description: string; symbol?: string }>(inclusions: Array<T>): Array<T> => {
    if (!isCharterBundleOffer) {
      return inclusions
    }

    const { originAirport, destinationAirport } = charterRouteInfo
    return rewriteCharterReturnFlightsGuestInclusions(inclusions, {
      rooms: filters?.rooms?.length ? filters.rooms : DEFAULT_CHARTER_SEARCH_ROOMS,
      originCity: originAirport.cityDesc,
      destinationCity: destinationAirport.cityDesc,
      intl,
    })
  }, [charterRouteInfo, filters?.rooms, intl, isCharterBundleOffer])

  const [
    featuresType,
    regularSearchTileFeatures,
    regularSearchTileFeaturesTitle,
  ] = useMemo(() => {
    const NoTitle = undefined // urgency label will be used instead

    if (offer.tileInclusions?.length) {
      const effectiveTileInclusions = getEffectiveInclusions(offer.tileInclusions)
      const tileInclusionsBoosted = reorderTileInclusionsByActiveFilters(effectiveTileInclusions, activeInclusionCategories)

      return [
        'inclusions',
        tileInclusionsBoosted,
        offer.tileInclusionsHeading,
      ] as const
    }

    if (offer.lowestPricePackage?.inclusions?.length) {
      const filtered = offer.lowestPricePackage.inclusions.filter((inclusion) => inclusion.displayContext === 'Rate')
      const boosted = reorderTileInclusionsByActiveFilters(getEffectiveInclusions(filtered), activeInclusionCategories)
      return [
        'inclusions',
        boosted,
        NoTitle,
      ] as const
    }

    if (offer.lowestPricePackage?.roomType.amenities.length) {
      return [
        'amenities',
        offer.lowestPricePackage.roomType.amenities,
        NoTitle,
      ] as const
    }

    const pkgWithInclusions = offer.packages.find((p) => p.inclusions?.length)
    const pkgInclusions = pkgWithInclusions?.inclusions?.filter((inclusion) => inclusion.displayContext === 'Rate')
    return [
      'amenities',
      pkgInclusions ? getEffectiveInclusions(pkgInclusions) : pkgInclusions,
      NoTitle,
    ] as const
  }, [offer, activeInclusionCategories, getEffectiveInclusions])

  const loyaltyCount = useLoyaltyBenefitInclusions(offer).length

  const maxLuxPlusInclusionsToDisplay = useMemo<number>(() => {
    if (!regularSearchTileFeatures?.length) return maxTotalInclusions
    return Math.max(maxTotalInclusions - regularSearchTileFeatures.length, MAX_LUX_PLUS_INCLUSIONS_TO_DISPLAY)
  }, [regularSearchTileFeatures, maxTotalInclusions])

  const maxRegularInclusionsToDisplay = useMemo<number>(() => {
    if (!displayedLuxPlusSearchTileInclusions.length) return maxTotalInclusions
    return Math.max(maxTotalInclusions - displayedLuxPlusSearchTileInclusions.length, MAX_REGULAR_INCLUSIONS_TO_DISPLAY)
  }, [displayedLuxPlusSearchTileInclusions, maxTotalInclusions])

  const offerUrl = useOfferUrl(offer, {
    filters,
    offerLinkIncludesFilters: true,
    bundledOfferId: offerMetaData?.bundledOfferId,
  })

  const showModal = useContext(ModalContext)
  const openAllInclusionsModal = useCallback(() => {
    if (doNotOpenInclusionsModal) return

    showModal(<Modal
      title={offer.property.name}
      primaryActionText={intl.formatMessage({
        id: 'yz1jWBepgJQnq1ifNEk6',
        defaultMessage: 'View offer',
        description: 'CTA button in hotel inclusions modal to view the offer',
      })}
      primaryActionTo={offerUrl}
    >
      <Group direction="vertical" gap={12}>
        {!!displayedLuxPlusSearchTileInclusions.length && <OfferInclusionsList type="lux-plus" inclusions={displayedLuxPlusSearchTileInclusions} />}
        <Group direction="vertical" gap={4}>
          {regularSearchTileFeatures && <OfferInclusionsList inclusions={regularSearchTileFeatures} />}
          <LoyaltyInclusions offer={offer} variant="offer-page" />
        </Group>
      </Group>
    </Modal>)
  }, [doNotOpenInclusionsModal, showModal, offer, offerUrl, displayedLuxPlusSearchTileInclusions, regularSearchTileFeatures, intl])

  return <Group direction="vertical" verticalAlign="space-between" gap={12} grow noDisplayWhenEmpty>
    <Group direction="vertical" gap={12} noDisplayWhenEmpty>
      {!!displayedLuxPlusSearchTileInclusions?.length && <LuxPlusInclusions
        luxPlusInclusions={displayedLuxPlusSearchTileInclusions}
        accommodationOffer={offer}
        hideUpsellModal={!!offer.luxPlusTileInclusions?.length}
        showMinStayDescription
        truncateAfter={maxLuxPlusInclusionsToDisplay}
        onShowMoreClick={openAllInclusionsModal}
      />}
      {featuresType === 'inclusions' && (!!regularSearchTileFeatures?.length || loyaltyCount > 0) && <Group direction="vertical" gap={4}>
        <SearchTileStackInclusionsList
          inclusions={regularSearchTileFeatures ?? []}
          inclusionsTitle={hideFeatureTitle ? undefined : regularSearchTileFeaturesTitle}
          truncateAfter={maxRegularInclusionsToDisplay}
          additionalHiddenCount={((regularSearchTileFeatures?.length ?? 0) + loyaltyCount) <= maxRegularInclusionsToDisplay ? 0 : loyaltyCount}
          onShowMoreClick={openAllInclusionsModal}
          // we want to show "Select packages only" label on search tile inclusions heading for SPP offers (which are LPC)
          // see https://aussiecommerce.atlassian.net/browse/HP-1012
          showSelectPackageOnlyLabel={offer.type === OFFER_TYPE_ALWAYS_ON}
        />
        {((regularSearchTileFeatures?.length ?? 0) + loyaltyCount) <= maxRegularInclusionsToDisplay && <LoyaltyInclusions offer={offer} variant="search-tile" />}
      </Group>}
      {featuresType === 'amenities' && <Group direction="vertical" gap={4} noDisplayWhenEmpty>
        {!!regularSearchTileFeatures?.length && <SearchTileStackAmenitiesList
          amenities={regularSearchTileFeatures}
          amenitiesTitle={hideFeatureTitle ? undefined : regularSearchTileFeaturesTitle}
          truncateAfter={maxAmenitiesToShow}
        />}
        <LoyaltyInclusions offer={offer} variant="search-tile" />
      </Group>}
    </Group>
    {!hideRoomBreakdown && <SearchTileRoomBreakdown
      offer={offer}
      filters={filters}
    />}
  </Group>
}

export default HotelSearchTileFeatures
